import React, { useState } from "react";
import { C } from "../constants/design";
import type { Provider } from "../lib/auth";
import { sendEmailOTP, verifyEmailOTP } from "../lib/auth";
import { useI18n } from "../i18n/I18nContext";
import type { Language } from "../i18n/I18nContext";

const NICK_RE = /^[A-Za-z0-9_.가-힣㄰-㆏ᄀ-ᇿ]{3,14}$/;

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

function validateNickname(n: string, t: (k: string) => string): string | null {
  const v = n.trim();
  if (v.length < 3) return t("login.atLeast3Chars");
  if (v.length > 14) return t("login.max14Chars");
  if (!NICK_RE.test(v)) return t("login.invalidChars");
  return null;
}

export function Login({
  onGuest,
  onOAuth,
}: {
  onGuest: (nickname: string, onDone: (error?: string) => void) => void;
  onOAuth: (provider: Provider, nickname: string) => void;
}) {
  const { t, language, setLanguage } = useI18n();
  const [nick, setNick] = useState("");
  const [nickError, setNickError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Email OTP flow
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);

  const handleGuest = () => {
    const err = validateNickname(nick, t);
    if (err) { setNickError(err); return; }
    setBusy(true);
    onGuest(nick.trim(), (error) => {
      setBusy(false);
      if (error) setNickError(error);
    });
  };

  const handleSendCode = async () => {
    const v = email.trim();
    if (!v || !v.includes("@")) { setEmailError(t("login.invalidEmail")); return; }
    setBusy(true);
    const ok = await sendEmailOTP(v);
    setBusy(false);
    if (ok) { setCodeSent(true); setEmailError(null); }
    else { setEmailError(t("login.sendFailed")); }
  };

  const handleVerifyCode = async () => {
    const raw = code.trim();
    if (raw.length < 4) { setCodeError(t("login.enterCodeFromEmail")); return; }
    setBusy(true);
    const session = await verifyEmailOTP(email.trim(), raw);
    setBusy(false);
    if (session) { window.location.reload(); }
    else { setCodeError(t("login.invalidCode")); }
  };

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "32px 22px",
        gap: 0,
        position: "relative",
      }}
    >
      {/* Language toggle */}
      <div
        style={{
          position: "absolute",
          top: 18,
          right: 18,
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.13)",
          borderRadius: 20,
          padding: "5px 8px 5px 10px",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, opacity: 0.7 }}>
          <path d="M12.87 15.07L10.33 12.56L10.36 12.53C12.0547 10.6475 13.3205 8.41951 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07ZM18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10ZM15.88 17L17.5 12.67L19.12 17H15.88Z" fill="white"/>
        </svg>
        <button
          onClick={() => setLanguage("en")}
          style={{
            background: language === "en" ? "rgba(255,255,255,0.18)" : "transparent",
            border: "none",
            borderRadius: 12,
            padding: "2px 6px",
            fontSize: 18,
            lineHeight: 1.3,
            cursor: "pointer",
            opacity: language === "en" ? 1 : 0.4,
            transition: "opacity 0.15s, background 0.15s",
          }}
        >
          🇺🇸
        </button>
        <button
          onClick={() => setLanguage("ko")}
          style={{
            background: language === "ko" ? "rgba(255,255,255,0.18)" : "transparent",
            border: "none",
            borderRadius: 12,
            padding: "2px 6px",
            fontSize: 18,
            lineHeight: 1.3,
            cursor: "pointer",
            opacity: language === "ko" ? 1 : 0.4,
            transition: "opacity 0.15s, background 0.15s",
          }}
        >
          🇰🇷
        </button>
        <button
          onClick={() => setLanguage("fr")}
          style={{
            background: language === "fr" ? "rgba(255,255,255,0.18)" : "transparent",
            border: "none",
            borderRadius: 12,
            padding: "2px 6px",
            fontSize: 18,
            lineHeight: 1.3,
            cursor: "pointer",
            opacity: language === "fr" ? 1 : 0.4,
            transition: "opacity 0.15s, background 0.15s",
          }}
        >
          🇫🇷
        </button>
      </div>

      {/* Logo */}
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ fontSize: 42, marginBottom: 4 }}>⚽🏆</div>
        <div className="d" style={{ fontSize: 30, fontWeight: 900, letterSpacing: -0.5 }}>
          Prono<span style={{ color: C.turf }}>Cup</span>
        </div>
      </div>

      {/* ── Section Guest ── */}
      <label style={{ fontSize: 12, fontWeight: 800, color: C.muted, marginBottom: 8 }}>
        {t("login.chooseNickname")}
      </label>
      <input
        value={nick}
        maxLength={14}
        onChange={(e) => { setNick(e.target.value); setNickError(null); }}
        onKeyDown={(e) => e.key === "Enter" && handleGuest()}
        placeholder={t("login.playerNamePlaceholder")}
        style={{
          background: C.surface,
          border: `1px solid ${nickError ? "#ef4444" : C.line}`,
          borderRadius: 12,
          padding: "13px 14px",
          color: C.text,
          fontSize: 15,
          outline: "none",
        }}
      />
      {nickError && (
        <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600, marginTop: 6 }}>
          {nickError}
        </div>
      )}
      <button
        onClick={handleGuest}
        disabled={busy}
        style={{
          marginTop: 12,
          border: "none",
          borderRadius: 12,
          padding: "15px",
          fontWeight: 600,
          fontSize: 16,
          background: "#48FF85",
          color: "#06121e",
          cursor: busy ? "default" : "pointer",
          opacity: busy ? 0.8 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {busy && (
          <span style={{
            width: 16,
            height: 16,
            border: "2.5px solid rgba(6,18,30,0.25)",
            borderTopColor: "#06121e",
            borderRadius: "50%",
            display: "inline-block",
            animation: "spin 0.7s linear infinite",
          }} />
        )}
        {busy ? t("login.joiningGame") : t("login.playAsGuest")}
      </button>

      {/* Divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "28px 0" }}>
        <div style={{ flex: 1, height: 1, background: C.line }} />
        <span style={{ fontSize: 12, color: C.faint, fontWeight: 700 }}>{t("login.or")}</span>
        <div style={{ flex: 1, height: 1, background: C.line }} />
      </div>

      {/* ── Section OAuth / Email ── */}
      {!emailMode ? (
        <>
          <button
            onClick={() => { setBusy(true); onOAuth("google", ""); }}
            disabled={busy}
            style={{
              border: "none",
              borderRadius: 12,
              height: 50,
              padding: "0 14px",
              fontWeight: 600,
              fontSize: 15,
              background: "#fff",
              color: "#1f1f1f",
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.6 : 1,
              width: "100%",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10, lineHeight: 1 }}>
              <GoogleIcon />
              <span style={{ lineHeight: 1 }}>{t("login.continueWithGoogle")}</span>
            </span>
          </button>
          <button
            onClick={() => setEmailMode(true)}
            style={{
              marginTop: 10,
              border: "none",
              borderRadius: 12,
              height: 50,
              padding: "0 14px",
              fontWeight: 600,
              fontSize: 15,
              background: "rgba(255,255,255,0.18)",
              color: "#fff",
              cursor: "pointer",
              width: "100%",
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10, lineHeight: 1 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              <span>{t("login.continueWithEmail")}</span>
            </span>
          </button>
          <p style={{ fontSize: 12, color: C.faint, textAlign: "center", margin: "10px 0 0" }}>
            {t("login.nicknameChosenAfterSignIn")}
          </p>
        </>
      ) : (
        <>
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
            onKeyDown={(e) => e.key === "Enter" && !codeSent && handleSendCode()}
            placeholder={t("login.emailPlaceholder")}
            style={{
              background: C.surface,
              border: `1px solid ${emailError ? "#ef4444" : C.line}`,
              borderRadius: 12,
              padding: "13px 14px",
              color: C.text,
              fontSize: 15,
              outline: "none",
            }}
          />
          {emailError && (
            <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600, marginTop: 6 }}>
              {emailError}
            </div>
          )}
          {!codeSent ? (
            <button
              onClick={handleSendCode}
              disabled={busy}
              style={{
                marginTop: 12,
                border: "none",
                borderRadius: 12,
                padding: "15px",
                fontWeight: 600,
                fontSize: 16,
                background: "#48FF85",
                color: "#06121e",
                cursor: busy ? "default" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? t("login.sending") : t("login.receiveCode")}
            </button>
          ) : (
            <>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 14, marginBottom: 8 }}>
                {t("login.codeSentTo", email)}
              </div>
              <input
                autoFocus
                value={code}
                maxLength={8}
                inputMode="numeric"
                onChange={(e) => { setCode(e.target.value); setCodeError(null); }}
                onKeyDown={(e) => e.key === "Enter" && handleVerifyCode()}
                placeholder={t("login.enterCodePlaceholder")}
                style={{
                  background: C.surface,
                  border: `1px solid ${codeError ? "#ef4444" : C.line}`,
                  borderRadius: 12,
                  padding: "13px 14px",
                  color: C.text,
                  fontSize: 20,
                  outline: "none",
                  letterSpacing: 6,
                  textAlign: "center",
                }}
              />
              {codeError && (
                <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 600, marginTop: 6 }}>
                  {codeError}
                </div>
              )}
              <button
                onClick={handleVerifyCode}
                disabled={busy}
                style={{
                  marginTop: 12,
                  border: "none",
                  borderRadius: 12,
                  padding: "15px",
                  fontWeight: 600,
                  fontSize: 16,
                  background: "#48FF85",
                  color: "#06121e",
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? t("login.verifying") : t("login.signIn")}
              </button>
            </>
          )}
          <button
            onClick={() => { setEmailMode(false); setCodeSent(false); setCode(""); setEmail(""); setEmailError(null); setCodeError(null); }}
            style={{
              marginTop: 12,
              background: "transparent",
              border: "none",
              color: C.faint,
              fontSize: 13,
              cursor: "pointer",
              padding: "4px 0",
            }}
          >
            {t("login.back")}
          </button>
        </>
      )}
    </div>
  );
}
